import React, { useState, useEffect } from 'react';
import { Sparkles, BookOpen, ArrowRight, Lock, Check, Star, Crown, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const VILLAGERS = [
  { key: 'cabrini', name: 'Cabrini', title: 'The Life Alchemist', img: '/villagers/cabrini.png',
    desc: 'Your guide through it all — transformation, wisdom, and a whole lot of truth.' },
  { key: 'fear', name: 'Fear', title: 'The Doubter', img: '/villagers/fear.png',
    desc: 'Questions everything and is certain something will go wrong. We invite him along anyway.' },
  { key: 'hot-flash', name: 'Hot Flash', title: 'The Heat of Change', img: '/villagers/hot-flash.png',
    desc: 'The unpredictable fire of transformation — especially for women in their power years.' },
  { key: 'anger', name: 'Anger', title: 'The Fire Starter', img: '/villagers/anger.png',
    desc: 'Frustration with what fell short — and the drive to burn past it into something new.' },
  { key: 'baby-joy', name: 'Baby Cabrini Joy', title: 'The Inner Knowing', img: '/villagers/baby-joy.png',
    desc: 'Young joy and insight that shows up to confirm: yes, this decision is right.' },
  { key: 'instigator', name: 'The Instigator', title: 'The Spark', img: '/villagers/instigator.png',
    desc: 'The nudge that dares you to begin — the little push that turns "someday" into today.' },
  { key: 'pecia', name: 'Pecia', title: 'The Keeper of Peace', img: '/villagers/pecia.png',
    desc: 'The calm in the change — she settles the noise so your truest answer can be heard.' },
  { key: 'hummingbird', name: 'The Hummingbird', title: 'The Messenger of Joy', img: '/villagers/hummingbird.png',
    desc: 'Light, swift, and fearless — proof that even the smallest wings can carry you forward.' },
];

const STATS = [
  { n: '1 Day', l: 'To Transform' },
  { n: '50+', l: 'Your People' },
  { n: '0', l: 'Tech Skills Needed' },
  { n: '100%', l: 'Real Talk' },
];

const WALKAWAY = [
  { title: 'Talk to AI Like You Mean It', desc: 'No jargon. No fear. You will know exactly what to say — and AI will actually listen.' },
  { title: 'Build Your Manifestor Profile', desc: 'Created once, used everywhere. ChatGPT, Claude, Gemini — they will all know who you are.' },
  { title: 'Create Content That Sounds Like YOU', desc: 'Posts, emails, ideas — generated in your voice, your style, your power.' },
  { title: 'Start or Grow Your Business With AI', desc: 'Your ideas are valid. Your experience is the asset. AI is the tool that helps you build it.' },
  { title: 'You Might Even Build Your OWN App!', desc: 'Yes. YOUR app. With AI as your co-builder, the idea you have been sitting on could become real.' },
];

const IS_FOR_YOU = [
  'You have been watching AI from the sidelines and you are DONE watching.',
  'You are 50+ and you refuse to be left behind by technology.',
  'You have an idea, a business, or a message — and you need help getting it out.',
  'You are tired of tech that talks down to you.',
  'You want to learn in a room where your experience is respected.',
  'You are ready to stop waiting and start creating.',
];

export default function GangstaAISeniorsHub() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [rName, setRName] = useState('');
  const [rEmail, setREmail] = useState('');
  const [reservingTier, setReservingTier] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const register = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/summit-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, source: 'summit_hub' }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setName(''); setEmail('');
        toast.success("You're on the list! Watch your inbox for summit updates. 🌙");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const reserve = async (tier) => {
    if (!rEmail) {
      toast.error('Please enter your email above so we can send your guide + details.');
      document.getElementById('summit-contact')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setReservingTier(tier);
    try {
      const res = await fetch(`${API}/api/summit-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: rName, email: rEmail, tier, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setReservingTier(null);
      }
    } catch {
      toast.error('Could not start checkout. Please try again.');
      setReservingTier(null);
    }
  };

  const buyEbook = async () => {
    setBuying(true);
    try {
      const res = await fetch(`${API}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: 'ebook-gais', origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setBuying(false);
      }
    } catch {
      toast.error('Could not start checkout. Please try again.');
      setBuying(false);
    }
  };

  return (
    <section className="gais-hub" data-testid="gais-hub">
      <div className="gais-hub-stars" aria-hidden="true" />

      {/* SUMMIT BANNER */}
      <a href="#pricing" className="gaish-banner" data-testid="gaish-banner">
        <Sparkles size={14} /> First-ever Gangsta AI for Seniors Summit — <strong>Saturday, September 12th · 11AM PST · Zoom</strong>
        <span className="gaish-banner-cta">Reserve Your Spot <ArrowRight size={13} /></span>
      </a>

      {/* HERO */}
      <header className="gaish-hero2" data-testid="gaish-hero">
        <div className="gaish-hero2-copy">
          <p className="gaish-eyebrow">Cabrini Schnyder · Life Alchemist · Author</p>
          <h1 className="gaish-hero2-title">
            I&apos;m talking to <em>YOU…</em>
          </h1>
          <p className="gaish-hero2-sub2">if you are done watching from the sidelines.</p>
          <p className="gaish-tagline">
            I see you. And I am here to walk you through it. A one-day workshop for women 50+
            who are done watching from the sidelines. No jargon. No fear. No tech degree required —
            just Cabrini, and the life you are ready to build.
          </p>
          <div className="gaish-hero-cta">
            <a href="#pricing" className="gaish-btn gaish-btn--gold" data-testid="gaish-hero-reserve">
              Yes, I Am Ready <ArrowRight size={16} />
            </a>
            <button type="button" onClick={buyEbook} disabled={buying}
              className="gaish-btn gaish-btn--ghost" data-testid="gaish-hero-ebook">
              {buying ? 'Redirecting…' : 'Get the eBook — $9.99'}
            </button>
          </div>
        </div>
        <div className="gaish-hero2-img" data-testid="gaish-hero-image">
          <img src="/summit/cabrini-pointing-nobg.png" alt="Cabrini Schnyder pointing at you" />
        </div>
      </header>

      {/* STATS */}
      <div className="gaish-stats" data-testid="gaish-stats">
        {STATS.map((s) => (
          <div className="gaish-stat" key={s.l}>
            <span className="gaish-stat-n">{s.n}</span>
            <span className="gaish-stat-l">{s.l}</span>
          </div>
        ))}
      </div>

      {/* THE TRUTH */}
      <div className="gaish-section gaish-truth" data-testid="gaish-truth">
        <p className="gaish-eyebrow">Let me tell you something</p>
        <h2 className="gaish-h2">
          You did not miss the AI revolution. <em>You were just waiting for the right teacher.</em>
        </h2>
        <p className="gaish-sub">
          Here is the truth: AI was not built for seniors. It was built by 25-year-olds for
          25-year-olds. The language is confusing. The tutorials assume you already know everything.
          And nobody is talking to the woman who has 40 years of wisdom and zero patience for nonsense.
        </p>
        <p className="gaish-sub"><strong>You are not behind. You are just at the door. Let me open it.</strong></p>
      </div>

      {/* WHAT YOU'LL WALK AWAY WITH */}
      <div className="gaish-section" data-testid="gaish-walkaway">
        <p className="gaish-eyebrow">One day. Zero confusion. All gangsta.</p>
        <h2 className="gaish-h2">What You Will Walk Away With</h2>
        <div className="gaish-cards">
          {WALKAWAY.map((c, i) => (
            <article className="gaish-card" key={i} data-testid={`walkaway-${i}`}>
              <span className="gaish-card-icon"><Star size={20} /></span>
              <h3 className="gaish-card-title">{c.title}</h3>
              <p className="gaish-card-desc">{c.desc}</p>
            </article>
          ))}
        </div>
      </div>

      {/* IS THIS YOU */}
      <div className="gaish-section gaish-isyou" data-testid="gaish-isyou">
        <p className="gaish-eyebrow">This is for you if…</p>
        <h2 className="gaish-h2">Does this sound <em>like you?</em></h2>
        <ul className="gaish-checklist">
          {IS_FOR_YOU.map((t, i) => (
            <li key={i} data-testid={`isyou-${i}`}><Check size={18} /> {t}</li>
          ))}
        </ul>
        <a href="#pricing" className="gaish-btn gaish-btn--gold gaish-btn--lg" data-testid="gaish-isyou-cta">
          Yes — That&apos;s Me <ArrowRight size={18} />
        </a>
      </div>

      {/* MEET THE VILLAGERS */}
      <div className="gaish-section" data-testid="gaish-villagers">
        <h2 className="gaish-h2">Meet the Alchemy Villagers</h2>
        <p className="gaish-sub">The cast of characters who walk this journey beside you.</p>
        <div className="gaish-villager-grid">
          {VILLAGERS.map((v) => (
            <article className="gaish-villager" key={v.key} data-testid={`villager-${v.key}`}>
              <div className="gaish-villager-frame">
                <img src={v.img} alt={v.name} loading="lazy" />
              </div>
              <h3 className="gaish-villager-name">{v.name}</h3>
              <p className="gaish-villager-title">{v.title}</p>
              <p className="gaish-villager-desc">{v.desc}</p>
            </article>
          ))}
        </div>
      </div>

      {/* PRICING — TWO DOORS + EBOOK */}
      <div className="gaish-section gaish-pricing" id="pricing" data-testid="gaish-pricing">
        <p className="gaish-eyebrow">The Alchemy Village Presents</p>
        <h2 className="gaish-h2">Ready to go deeper? Join Cabrini <em>LIVE.</em></h2>
        <p className="gaish-sub">Two ways in. Both include your Companion Guide <strong>FREE</strong>.</p>

        {/* shared contact */}
        <div className="gaish-contact" id="summit-contact" data-testid="summit-contact">
          <p className="gaish-contact-label"><Lock size={13} /> Where should we send your guide + details?</p>
          <div className="gaish-contact-fields">
            <input type="text" placeholder="First name" value={rName}
              onChange={(e) => setRName(e.target.value)} className="gaish-input"
              data-testid="summit-contact-name" aria-label="First name" />
            <input type="email" placeholder="Your email" value={rEmail} required
              onChange={(e) => setREmail(e.target.value)} className="gaish-input"
              data-testid="summit-contact-email" aria-label="Email" />
          </div>
        </div>

        <div className="gaish-doors">
          {/* SUMMIT DOOR — $49 */}
          <article className="gaish-door" data-testid="tier-summit">
            <p className="gaish-door-kicker">One Day</p>
            <h3 className="gaish-door-name">The Summit</h3>
            <p className="gaish-door-when">Saturday, September 12th · 11am PST · Live on Zoom</p>
            <p className="gaish-door-desc">
              One powerful day with Cabrini. Your AI tools activated. Your Companion Guide included FREE.
            </p>
            <p className="gaish-door-price" data-testid="summit-price">$49</p>
            <button type="button" onClick={() => reserve('summit')} disabled={reservingTier === 'summit'}
              className="gaish-btn gaish-door-btn gaish-door-btn--maroon" data-testid="summit-reserve-btn">
              {reservingTier === 'summit' ? 'Redirecting…' : <>Reserve My Spot <ArrowRight size={15} /></>}
            </button>
          </article>

          {/* PLATINUM DOOR — $997 */}
          <article className="gaish-door gaish-door--platinum" data-testid="tier-platinum">
            <span className="gaish-door-badge"><Crown size={13} /> Platinum</span>
            <p className="gaish-limited" data-testid="platinum-limited">
              <Users size={14} /> Limited to 25 Manifestors
            </p>
            <p className="gaish-door-kicker">One Full Month · 5 Live Sessions</p>
            <h3 className="gaish-door-name">Platinum Out Front</h3>
            <p className="gaish-door-when">The Summit + four live weekly sessions — with Cabrini</p>
            <p className="gaish-door-desc">
              Wake up at the Summit, then build for four weeks straight: your own AI agent, your
              business plan, your marketing voice, and your live website — all by month&apos;s end.
              Companion Guide included FREE.
            </p>
            <p className="gaish-door-price" data-testid="platinum-price">$1,997</p>
            <button type="button" onClick={() => reserve('platinum')} disabled={reservingTier === 'platinum'}
              className="gaish-btn gaish-door-btn gaish-door-btn--gold" data-testid="platinum-reserve-btn">
              {reservingTier === 'platinum' ? 'Redirecting…' : <>I Want the Full Month <ArrowRight size={15} /></>}
            </button>
          </article>
        </div>

        <p className="gaish-secure"><Lock size={12} /> Secure checkout via Stripe · One-time payment</p>

        {/* eBook standalone — $9.99 */}
        <div className="gaish-ebook" data-testid="tier-ebook">
          <div className="gaish-ebook-cover">
            <img src="/summit/guide-cover.png" alt="Gangsta AI for Seniors eBook cover" loading="lazy" />
          </div>
          <div className="gaish-ebook-copy">
            <p className="gaish-tier-kicker"><BookOpen size={14} /> The eBook</p>
            <h3 className="gaish-tier-name">Just want the book?</h3>
            <p className="gaish-tier-desc">
              Get the No-Jargon, No-Fear Companion Guide on its own — the same teaching book Cabrini
              uses at the Summit. Yours to keep and print. (Or register for the Summit and it&apos;s FREE.)
            </p>
            <div className="gaish-ebook-buy">
              <span className="gaish-price" data-testid="ebook-price">$9.99</span>
              <button type="button" onClick={buyEbook} disabled={buying}
                className="gaish-btn gaish-btn--ghost" data-testid="ebook-buy-btn">
                {buying ? 'Redirecting…' : <>Get the Guide — $9.99 <ArrowRight size={16} /></>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CABRINI'S LAW */}
      <div className="gaish-section gaish-law" data-testid="gaish-law">
        <p className="gaish-eyebrow">Cabrini&apos;s Law</p>
        <h2 className="gaish-h2 gaish-law-title">F.U.C.K. T.H.A.T.T.</h2>
        <p className="gaish-law-expand">
          Finally Understanding Common Knowledge — Things Happen All The Time
        </p>
        <p className="gaish-sub">
          This is not a slogan. This is a way of living. Every day you are manifesting something —
          good or bad. Cabrini teaches you how to get to the GOOD, and find the GOD in YOU.
        </p>
        <p className="gaish-quote">
          “The greatest guide you will ever have is the God in YOU.” <span>— Cabrini Schnyder</span>
        </p>
      </div>

      {/* WORKROOM CTA — free intro */}
      <div className="gaish-section gaish-workroom-cta" data-testid="gaish-workroom-cta">
        <h2 className="gaish-h2">Get a free taste first</h2>
        <p className="gaish-sub">
          Step into the private <strong>Three Core Questions Workroom</strong> — a free guided
          reflection in Cabrini&apos;s voice. Answer the three core questions and feel what&apos;s coming at the summit.
        </p>
        <a href="/gangsta-ai-seniors/" className="gaish-btn gaish-btn--gold gaish-btn--lg"
           data-testid="gaish-enter-workroom">
          Enter the Free Workroom <ArrowRight size={18} />
        </a>
      </div>

      {/* FREE LIST SIGNUP */}
      <div className="gaish-section gaish-freelist" data-testid="gaish-freelist">
        <h2 className="gaish-h2">Not ready yet?</h2>
        <p className="gaish-sub">
          Join the free list — be the first to know when the workshop opens, when the eBook drops,
          and when Cabrini has something you need to hear.
        </p>
        <form className="gaish-form gaish-form--inline" onSubmit={register} data-testid="gais-list-form">
          <input type="text" placeholder="First name" value={name}
            onChange={(e) => setName(e.target.value)} className="gaish-input"
            data-testid="gais-list-name" aria-label="First name" />
          <input type="email" placeholder="Your email" value={email} required
            onChange={(e) => setEmail(e.target.value)} className="gaish-input"
            data-testid="gais-list-email" aria-label="Email" />
          <button type="submit" className="gaish-btn gaish-btn--ghost" disabled={loading}
            data-testid="gais-list-btn">
            {loading ? 'Joining…' : 'Join the List'}
          </button>
        </form>
      </div>
    </section>
  );
}
